import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import { formatServiceLevelKey } from '~/types/CarrierAccount';
import Rate, { attributePopularRateV2, RateObjectId } from '~/types/Rate';
import { ShipmentObjectId } from '~/types/Shipment';

export type SelectedRates = Record<ShipmentObjectId, RateObjectId>;

export type RatesState = {
  readonly data: { [rateId: string]: Readonly<Rate> };
  readonly byShipmentId: Record<ShipmentObjectId, readonly RateObjectId[]>;
  readonly selectedRates: SelectedRates;
  // Stores if a rate selection for a given shipment was made by the user or automatically by us
  readonly userTouchedRates: Record<ShipmentObjectId, boolean>;
};

const initialState: RatesState = {
  data: {},
  byShipmentId: {},
  selectedRates: {},
  userTouchedRates: {},
};

function reducer(state: RatesState = initialState, action: ActionTypes): RatesState {
  switch (action.type) {
    case ActionKeys.SHIPMENT_FETCH_SUCCESS:
      const {
        shipment: { rates: ratesFromAPI },
      } = action.payload;
      const rates = attributePopularRateV2(ratesFromAPI);
      return {
        ...state,
        data: {
          ...state.data,
          ...rates.reduce((obj, rate) => ({ ...obj, [rate.objectId]: rate }), {}),
        },
        byShipmentId: {
          ...state.byShipmentId,
          [action.shipmentId]: rates.map((rate) => rate.objectId),
        },
      };
    case ActionKeys.MULTIPLE_RATE_SELECT:
      const newSelectedRates = Object.keys(action.payload.rateSelection).reduce<SelectedRates>(
        (obj, shipmentobjectId) => {
          const rateIds = state.byShipmentId[shipmentobjectId];
          const foundRateByServiceLevel =
            rateIds &&
            rateIds
              .map((rateId) => state.data[rateId])
              .find((rate) => {
                const {
                  carrierAccount,
                  servicelevel: { token },
                } = rate;
                const rateToken =
                  carrierAccount && !action.payload.shouldIgnoreCarrier
                    ? formatServiceLevelKey(carrierAccount, token)
                    : token;
                return rateToken === action.payload.serviceLevelKey;
              });

          if (foundRateByServiceLevel) {
            return {
              ...obj,
              [shipmentobjectId]: foundRateByServiceLevel.objectId,
            };
          }
          return {
            ...obj,
          };
        },
        {},
      );

      let newUserTouchedRates: Record<ShipmentObjectId, boolean> = {};
      for (const shipmentObjectId of Object.keys(newSelectedRates)) {
        newUserTouchedRates[shipmentObjectId] = true;
      }

      return {
        ...state,
        selectedRates: {
          ...state.selectedRates,
          ...newSelectedRates,
        },
        userTouchedRates: {
          ...state.userTouchedRates,
          ...newUserTouchedRates,
        },
      };
    case ActionKeys.RATE_SELECT:
      return {
        ...state,
        selectedRates: {
          ...state.selectedRates,
          [action.payload.shipmentObjectId]: action.payload.rateObjectId,
        },
        userTouchedRates: {
          ...state.userTouchedRates,
          [action.payload.shipmentObjectId]: action.payload.userTriggered,
        },
      };
    // Resets state after the package information is saved, this cleans
    // the state when the user re-sends the batch information
    case ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION:
      return initialState;
    case ActionKeys.ORDERS_BATCH_REMOVE_SELECTED_RATE:
      const { [action.shipmentId]: unwantedRates, ...ratesByShipmentId } = state.byShipmentId;
      const cleanedRates = Object.keys(state.data).reduce((rates, rateId) => {
        if (unwantedRates.includes(rateId)) {
          return rates;
        }

        return {
          ...rates,
          [rateId]: state.data[rateId],
        };
      }, {});

      return {
        ...state,
        data: {
          ...cleanedRates,
        },
        byShipmentId: {
          ...ratesByShipmentId,
        },
      };

    case ActionKeys.RATE_CLEAR:
      const byShipmentId = { ...state.byShipmentId };
      const userTouchedRates = { ...state.userTouchedRates };
      const selectedRates = { ...state.selectedRates };

      action.payload.shipmentIds.forEach((id) => {
        delete byShipmentId[id];
        delete userTouchedRates[id];
        delete selectedRates[id];
      });

      return {
        ...state,
        byShipmentId,
        userTouchedRates,
        selectedRates,
      };

    default:
      return state;
  }
}

export default reducer;
